import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { ThemeProvider, ToastProvider } from '@paige/ui';
import '@paige/ui/styles.css';
import { routeTree } from './routeTree.gen';
import { AuthBoundary } from './auth/AuthBoundary';
import { AuthStore } from './auth/auth-store';
import { forgetOnSignOut } from './auth/forget-on-sign-out';
import { DataSourceProvider } from './api/data-source';
import { createApiDataSource } from './api/api-data-source';
import { ApiError } from './api/http';
import { isRefusalOnPage } from './api/refusal-on-page';
import { sessionRefused } from './shell/session-refused';
import { noteReadOnlyRefusal } from './shell/ReadOnlyRefusals';
import { ScreenFailure, reportFailure } from './screens/failure/ScreenFailure';
import { captureAcquisition } from './analytics/acquisition';
import { capturePartnerLink } from './partners/partner-code';
import { setProductTracker, setScanProblemReporter } from '@paige/capturepoint';
import { startAnalytics, track } from './analytics/events';
import { hideTestsMiddleware } from './screens/admin/hide-tests';
import { frictionMiddleware, watchBrowserErrors } from './analytics/friction';
import { reportClientError, watchClientErrors } from './analytics/client-errors';
import { trackSessionStart } from './analytics/session';
import { watchScreens } from './analytics/screens';
import { watchTitles } from './shell/page-title';
import { UpdateBanner } from './version/UpdateBanner';
import { reloadHold } from './version/reload-hold';
import { watchForUpdates } from './version/watch-for-updates';
import './styles/app.css';

// The first visit's campaign and referrer, remembered for the sign-up (R9 a).
captureAcquisition();
capturePartnerLink();

// The API is `/v1` on the same origin: Vite proxies it to VITE_API_ORIGIN (tools/fixture-api in
// development), and production serves the app and the API behind one host.
const build = import.meta.env['VITE_BUILD'] ?? 'dev';
const dataSource = createApiDataSource({ baseUrl: '/v1', clientVersion: build });
const auth = new AuthStore(dataSource);

/**
 * A 401 anywhere asks `GET /me` what it means (shell/session-refused.ts): the session ended, or the
 * person is no longer in the company the request named. A 403 `support_grant_read_only` is said in the
 * server's words. A 403 `mfa_required` means the company asks
 * for a second step this session never took: the session is over, and sign-in (with the way back)
 * enrols the person if need be and asks for the code.
 */
const onUnauthorized = sessionRefused({
  auth,
  here: () => `${window.location.pathname}${window.location.search}`,
  go: (where) => void router.navigate(where),
});

function onRequestFailure(e: unknown): void {
  if (!(e instanceof ApiError)) return;
  if (e.unauthenticated) onUnauthorized();
  // A read-only support view's write: the server's own sentence (shell/ReadOnlyRefusals.tsx).
  noteReadOnlyRefusal(e);
  if (e.secondFactorRequired) {
    auth.requireSecondFactor();
    const next = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    void router.navigate({ to: '/sign-in', search: { next } });
  }
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
  queryCache: new QueryCache({ onError: onRequestFailure }),
  mutationCache: new MutationCache({
    onError: (e, _variables, _result, mutation) => {
      // Allowing a device: its page says the refusal, and the session is asked, not assumed over.
      if (isRefusalOnPage(e, mutation.meta)) {
        void auth.recheck();
        return;
      }
      onRequestFailure(e);
    },
  }),
});

/**
 * The error screen is the router's default rather than the root route's `errorComponent` for two
 * reasons. A default gives every match a boundary of its own, so the route that failed is the one
 * replaced and the frames above it live on; a boundary only on the root would take the whole page
 * down whichever route threw. And `autoCodeSplitting` puts a route file's `errorComponent` in a
 * chunk of its own, which cannot be fetched when the thing that failed was fetching a chunk — the
 * failure this was raised for. Imported here, the error screen is in the entry bundle and is
 * already loaded before anything can go wrong.
 */
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultErrorComponent: ScreenFailure,
  // The console for a developer, the API's log for whoever is on call (analytics/client-errors.ts).
  defaultOnCatch: (error, info) => {
    reportFailure(error, info);
    reportClientError(error, 'render');
  },
  context: { auth, ds: dataSource, queryClient },
});

// The next screen has arrived: a person who signed out is no longer on the screen they left.
router.subscribe('onResolved', () => auth.arrived());
// What was fetched for one person is never shown to the next on this computer.
forgetOnSignOut(auth, queryClient);

// The product event stream (R9 b, R10): screen views from the router, the session's start, refused
// requests and uncaught errors, batches to POST /v1/events.
startAnalytics(dataSource.client);
watchScreens(router);
watchTitles(router, queryClient);
// The workbench package's own flows (intake, scan, export) report through the same stream.
setProductTracker((name, detail) => track(name, { feature: name, ...(detail ? { detail } : {}) }));
// A scan that ended badly on this computer goes to the API's log as scanner_error, for paige-watch and support.
setScanProblemReporter((message) => reportClientError(message, 'scanner'));
dataSource.client.use(frictionMiddleware());
// The admin area's "Hide test companies" switch rides on every admin read that takes it.
dataSource.client.use(hideTestsMiddleware());
watchBrowserErrors();
watchClientErrors(dataSource.client, build);
trackSessionStart();

// A tab left open over a deploy moves onto the new Paige, never while work in it could be lost
// (src/version, docs/engineering/web.md, "When Paige is updated").
watchForUpdates(router);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root missing from index.html');

createRoot(rootEl).render(
  <StrictMode>
    <ThemeProvider>
      <ToastProvider>
        <AuthBoundary store={auth}>
          <DataSourceProvider value={dataSource}>
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
            </QueryClientProvider>
          </DataSourceProvider>
        </AuthBoundary>
        <UpdateBanner held={reloadHold} reload={() => window.location.reload()} />
      </ToastProvider>
    </ThemeProvider>
  </StrictMode>,
);
